// ═══════════════════════════════════════════════════════════
// Root Error Boundary
// Catches React render errors that would otherwise blank the app inside
// the Bitrix24 iframe. Reports to the local dev trace store in dev
// (see dev-error-reporter.ts); always shows a recoverable fallback.
// ═══════════════════════════════════════════════════════════
import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportReactError } from "@/lib/dev-error-reporter";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class DevErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportReactError(error, info.componentStack ?? "");
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, fontFamily: "sans-serif" }}>
          <h2 style={{ margin: 0, marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: "#666", marginBottom: 16 }}>{this.state.error.message}</p>
          <button
            onClick={() => {
              this.setState({ error: null });
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
